/**
 * Router — route table, auth guard and the OAuth callback.
 *
 * Routes (contract §7 has the matching API surface):
 *
 *   /                      → redirect to /runs
 *   /dashboard             Dashboard.tsx        what is wrong now, and what is about to be
 *   /runs                  Runs.tsx
 *   /runs/:id              RunDetail.tsx        useParams<{ id: string }>()
 *   /sent                  Sent.tsx             ?message=<id> opens the viewer
 *   /downloads             Downloads.tsx
 *   /schedules             Schedules.tsx
 *   /clients               Clients.tsx
 *   /clients/:id           ClientDetail.tsx     :id is the client_id slug
 *   /dispositions          Dispositions.tsx     picks a client in-page
 *   /dispositions/:clientId Dispositions.tsx    deep link from a client or a run
 *   /auth/callback         hosted-UI return leg — not inside the app shell
 *   *                      NotFound
 *
 * Screen agents: replace the file in src/pages, keep the default export, and do not
 * edit this table unless you are adding a sub-route under your own screen.
 *
 * Deployment note: these are real paths, not hashes. Whatever serves the bundle must
 * rewrite unknown paths to index.html (CloudFront: custom error response 403/404 →
 * /index.html with status 200) or a refresh on /runs/abc123 returns S3's 404.
 */

import { useEffect, useState } from 'react';
import Login from './pages/Login';
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  useNavigate,
  useRouteError,
} from 'react-router-dom';
import type { ReactNode } from 'react';

import Layout from '@/components/Layout';
import Button from '@/components/Button';
import EmptyState from '@/components/EmptyState';
import { authConfig, beginSignIn, completeSignIn, isConfigured, useAuth } from '@/lib/auth';

import ClientDetail from '@/pages/ClientDetail';
import Clients from '@/pages/Clients';
import Dashboard from '@/pages/Dashboard';
import Dispositions from '@/pages/Dispositions';
import Downloads from '@/pages/Downloads';
import RunDetail from '@/pages/RunDetail';
import Runs from '@/pages/Runs';
import Schedules from '@/pages/Schedules';
import Sent from '@/pages/Sent';

// ---------------------------------------------------------------------------
// Boot screens
// ---------------------------------------------------------------------------

function BootCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="boot-screen">
      <div className="boot-card">
        <h1 style={{ fontSize: 'var(--fs-lg)' }}>{title}</h1>
        {children}
      </div>
    </div>
  );
}

/**
 * Shown when the bundle was built without Cognito settings. A blank white page here is
 * indistinguishable from a broken deploy, so say exactly which variables are missing.
 */
function NotConfigured() {
  const missing = [
    authConfig.domain ? null : 'VITE_COGNITO_DOMAIN',
    authConfig.clientId ? null : 'VITE_COGNITO_CLIENT_ID',
  ].filter(Boolean);

  return (
    <BootCard title="Console is not configured">
      <p className="muted">
        This build has no Cognito settings, so it cannot sign anyone in. Copy{' '}
        <code>web/.env.example</code> to <code>web/.env.local</code> and fill in:
      </p>
      <div className="code-block">{missing.join('\n')}</div>
      <p className="small dim">
        The app client must be a public client with no secret — the console uses the
        authorization-code flow with PKCE.
      </p>
    </BootCard>
  );
}

// ---------------------------------------------------------------------------
// Guard
// ---------------------------------------------------------------------------

/**
 * Bounces anonymous operators to the hosted UI, remembering where they were headed.
 * Rendering happens only once a session exists, so no screen ever fires an API call
 * without a bearer token.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();

  if (!isConfigured() && !authConfig.disabled) return <NotConfigured />;

  // 'unknown' means the stored session has not been read yet. Rendering the login
  // form during that beat would flash it at someone who is already signed in.
  if (status === 'unknown') {
    return (
      <BootCard title="Loading">
        <div className="cluster">
          <span className="spinner spinner-lg" aria-hidden="true" />
          <span className="muted">Checking your session…</span>
        </div>
      </BootCard>
    );
  }

  // The console signs in on its own page rather than bouncing to the Hosted UI,
  // whose markup is AWS's and cannot carry this design. See pages/Login.tsx.
  if (status !== 'authenticated') return <Login />;

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// OAuth callback
// ---------------------------------------------------------------------------

/**
 * The PKCE verifier is single-use: completeSignIn() consumes it from sessionStorage.
 * StrictMode double-invokes effects in development, and a remount would otherwise hit
 * a consumed verifier and report a bogus state mismatch — so the exchange promise is
 * memoised at module scope and every caller awaits the same one.
 */
let exchangeInFlight: ReturnType<typeof completeSignIn> | null = null;

function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    exchangeInFlight = exchangeInFlight ?? completeSignIn();

    exchangeInFlight
      .then((result) => {
        if (cancelled) return;
        // replace: the callback URL carries a used code and must not stay in history.
        navigate(result.returnTo || '/', { replace: true });
      })
      .catch((err: unknown) => {
        exchangeInFlight = null;
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Sign-in failed.');
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (error) {
    return (
      <BootCard title="Sign-in did not complete">
        <p className="muted">{error}</p>
        <Button
          variant="primary"
          onClick={() => {
            exchangeInFlight = null;
            void beginSignIn('/');
          }}
        >
          Try again
        </Button>
      </BootCard>
    );
  }

  return (
    <BootCard title="Completing sign-in">
      <div className="cluster">
        <span className="spinner spinner-lg" aria-hidden="true" />
        <span className="muted">Exchanging the authorization code…</span>
      </div>
    </BootCard>
  );
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** Catches a throw from any route element so a screen bug does not blank the console. */
function RouteError() {
  const error = useRouteError();
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unexpected error.';

  return (
    <div className="app-content">
      <EmptyState
        tone="danger"
        title="This screen failed to render"
        description={message}
        action={
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload
          </Button>
        }
        secondaryAction={
          <Button onClick={() => window.location.assign('/runs')}>Back to runs</Button>
        }
      />
    </div>
  );
}

function NotFound() {
  return (
    <EmptyState
      title="No such page"
      description="That route does not exist in the dispatch console."
      action={
        <Button variant="primary" onClick={() => window.location.assign('/runs')}>
          Go to runs
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Route table
// ---------------------------------------------------------------------------

export const router = createBrowserRouter([
  {
    path: '/auth/callback',
    element: <AuthCallback />,
    errorElement: <RouteError />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      // Runs is the landing page: the first question every morning is "did last night go out?"
      // Mounting Dashboard is additive; moving the landing page onto it is a product
      // decision, not a design one, so it is flagged and not taken here.
      { index: true, element: <Navigate to="/runs" replace /> },

      // 1,028 lines of built screen that no URL reached: it answers the five morning
      // questions (stale vs failed, bouncing recipients, links dying in 24h, unranked
      // dispositions), and two of those have no other surface at all.
      { path: 'dashboard', element: <Dashboard /> },

      { path: 'runs', element: <Runs /> },
      { path: 'runs/:id', element: <RunDetail /> },
      { path: 'sent', element: <Sent /> },
      { path: 'downloads', element: <Downloads /> },
      { path: 'schedules', element: <Schedules /> },
      { path: 'clients', element: <Clients /> },
      { path: 'clients/:id', element: <ClientDetail /> },
      { path: 'dispositions', element: <Dispositions /> },
      { path: 'dispositions/:clientId', element: <Dispositions /> },

      { path: '*', element: <NotFound /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}

export default AppRouter;
